import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { ReaderHost, Reciter } from './readerHost';

const asHtml = (html: string) => ({ __html: html });

const PAUSE = '<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
const PLY = '<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5l12 7-12 7z"/></svg>';
const BACK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zM20 6v12l-9-6z"/></svg>';
const FWD = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 6h2v12h-2zM4 6l9 6-9 6z"/></svg>';
const REP = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>';

const SPEEDS = ['1×', '1.25×', '1.5×', '1.75×', '2×', '0.75×'];
const RATES = [1, 1.25, 1.5, 1.75, 2, 0.75];

interface PromptProps {
  host: ReaderHost;
  onChoose: (fromSurah: boolean) => void;
}

/** "A new surah begins on screen": recite from the surah, or from the top of the page (legacy audioPrompt). */
export function RecitationPrompt({ host, onChoose }: PromptProps) {
  const t = host.t;
  return (
    <div className="apprompt">
      <b dangerouslySetInnerHTML={asHtml(t('newSurahT'))} />
      <span dangerouslySetInnerHTML={asHtml(t('newSurahB'))} />
      <div className="apbtns">
        <button className="apsurah" onClick={() => onChoose(true)} dangerouslySetInnerHTML={asHtml(t('startSurahBtn'))} />
        <button className="aptop" onClick={() => onChoose(false)} dangerouslySetInnerHTML={asHtml(t('startTopBtn'))} />
      </div>
    </div>
  );
}

interface PlayerProps {
  host: ReaderHost;
  queue: string[];
  /** The verse now reciting, or null when nothing is. */
  onVerse: (id: string | null) => void;
  onClose: () => void;
}

/**
 * The bottom bar as a recitation player (legacy audioOpen): the queue plays
 * through the application's one <audio> element, the track shows the
 * position in the queue and the label the verse key, with repeat (all / one),
 * previous / next, speed and a reciter list. Unmounting is the one exit: it
 * stops the audio, so a page submitted mid-recitation goes quiet (the legacy
 * player kept reciting behind the donation screen).
 */
export function RecitationPlayer({ host, queue, onVerse, onClose }: PlayerProps) {
  const [qi, setQi] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [repeat, setRepeat] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [fraction, setFraction] = useState(0);
  const [reciters, setReciters] = useState<Reciter[]>([]);
  const [reciterName, setReciterName] = useState('…');
  const [listOpen, setListOpen] = useState(false);

  const qiRef = useRef(0);
  const playingRef = useRef(true);
  const repeatRef = useRef(0);
  const speedRef = useRef(0);
  const alive = useRef(true);
  const onVerseRef = useRef(onVerse);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onVerseRef.current = onVerse; onCloseRef.current = onClose; });

  const nameOf = (id: number) => {
    const r = reciters.find((x) => x.id === id);
    return r ? r.reciter_name ?? 'Reciter' : 'Reciter';
  };

  const playIdx = async () => {
    if (!alive.current) return;
    if (qiRef.current < 0) qiRef.current = 0;
    if (qiRef.current >= queue.length) { onCloseRef.current(); return; }
    const key = queue[qiRef.current];
    setQi(qiRef.current);
    onVerseRef.current(key);
    try {
      const audio = host.audio();
      const url = await audio.verseAudioUrl(key);
      if (!alive.current) return;
      if (!url) { qiRef.current++; void playIdx(); return; }
      const el = audio.element;
      el.src = url; el.playbackRate = RATES[speedRef.current];
      if (playingRef.current) await el.play();
      if (queue[qiRef.current + 1]) audio.verseAudioUrl(queue[qiRef.current + 1]).catch(() => {});
    } catch (error: unknown) { console.error('audio', error); }
  };

  useEffect(() => {
    alive.current = true;
    const audio = host.audio();
    const el = audio.element;
    const onEnd = () => {
      if (repeatRef.current === 2) { el.currentTime = 0; el.play().catch(() => {}); return; }
      qiRef.current++;
      if (qiRef.current >= queue.length && repeatRef.current === 1) qiRef.current = 0;
      void playIdx();
    };
    el.addEventListener('ended', onEnd);
    const tick = window.setInterval(() => {
      setFraction(el.duration > 0 ? Math.min(1, el.currentTime / el.duration) : 0);
    }, 300);
    audio.loadReciters().then((list) => {
      if (!alive.current) return;
      setReciters(list);
      const r = list.find((x) => x.id === audio.reciterId());
      setReciterName(r ? r.reciter_name ?? 'Reciter' : 'Reciter');
      void playIdx();
    });
    return () => {
      alive.current = false;
      clearInterval(tick);
      el.removeEventListener('ended', onEnd);
      el.pause();
      el.removeAttribute('src');
      onVerseRef.current(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlay = () => {
    playingRef.current = !playingRef.current;
    setPlaying(playingRef.current);
    const el = host.audio().element;
    if (playingRef.current) el.play().catch(() => {}); else el.pause();
  };
  const previous = () => { qiRef.current = Math.max(0, qiRef.current - 1); void playIdx(); };
  const next = () => { qiRef.current++; void playIdx(); };
  const cycleRepeat = () => { repeatRef.current = (repeatRef.current + 1) % 3; setRepeat(repeatRef.current); };
  const cycleSpeed = () => {
    speedRef.current = (speedRef.current + 1) % SPEEDS.length; setSpeed(speedRef.current);
    host.audio().element.playbackRate = RATES[speedRef.current];
  };
  const toggleList = (e: MouseEvent) => { e.stopPropagation(); setListOpen((open) => !open); };
  const chooseReciter = (id: number) => {
    host.audio().setReciterId(id);
    setReciterName(nameOf(id));
    setListOpen(false);
    void playIdx();
  };
  const currentReciter = host.audio().reciterId();

  const width = (((qi + fraction) / queue.length) * 100).toFixed(1) + '%';
  return (
    <div className="apwrap">
      <div className="aptop">
        <div className="aptrack"><i style={{ width }} /></div>
        <span className="aptime">{queue[qi] || ''}</span>
      </div>
      <div className="apctl">
        <button className={'aprep' + (repeat > 0 ? ' on' : '') + (repeat === 2 ? ' one' : '')} aria-label="Repeat" onClick={cycleRepeat} dangerouslySetInnerHTML={asHtml(REP + '<i class="reponly">1</i>')} />
        <button className="apback" aria-label="Previous verse" onClick={previous} dangerouslySetInnerHTML={asHtml(BACK)} />
        <button className="applay" aria-label={playing ? 'Pause' : 'Play'} onClick={togglePlay} dangerouslySetInnerHTML={asHtml(playing ? PAUSE : PLY)} />
        <button className="apfwd" aria-label="Next verse" onClick={next} dangerouslySetInnerHTML={asHtml(FWD)} />
        <span className="apsp" />
        <button className="aprec" aria-label="Reciter" onClick={toggleList}><span>{reciterName}</span> ▾</button>
        <button className="apspeed" onClick={cycleSpeed}>{SPEEDS[speed]}</button>
        <button className="apx" aria-label="Stop" onClick={onClose}>✕</button>
      </div>
      {listOpen && (
        <div className="apreclist">
          {reciters.map((r) => (
            <button key={r.id} className={'apreci' + (r.id === currentReciter ? ' on' : '')} data-id={r.id} onClick={() => chooseReciter(r.id)}>
              {(r.reciter_name ?? '') + (r.style ? ' · ' + r.style : '')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
